/**
 * Standardized styling constants for docs components.
 * Ensures visual consistency across all docs pages.
 */

// Color palette
export const colors = {
  primary: '#0ea5e9',
  primaryHover: '#0284c7',
  text: '#1f2937',
  textSecondary: '#4b5563',
  muted: '#6b7280',
  border: 'rgba(0, 0, 0, 0.1)',
  borderLight: 'rgba(0, 0, 0, 0.06)',
  background: '#ffffff',
  backgroundHover: '#f9fafb',
  backgroundActive: '#f3f4f6',
  link: '#0ea5e9',
  linkHover: '#0284c7',
  primaryLight: 'rgba(14, 165, 233, 0.1)'
};

// Typography
export const typography = {
  h1: {
    fontSize: '28px',
    fontWeight: 'bold',
    lineHeight: 1.2,
    marginBottom: '16px',
    color: colors.text
  },
  h2: {
    fontSize: '22px',
    fontWeight: '600',
    lineHeight: 1.3,
    marginBottom: '12px',
    marginTop: '0',
    color: colors.text
  },
  h3: {
    fontSize: '18px',
    fontWeight: '600',
    lineHeight: 1.4,
    marginBottom: '8px',
    marginTop: '0',
    color: colors.text
  },
  body: {
    fontSize: '14px',
    lineHeight: 1.6,
    color: colors.text
  },
  small: {
    fontSize: '12px',
    lineHeight: 1.5,
    color: colors.muted
  }
};

// Spacing (8px base unit)
export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px'
};

// Containers
export const containers = {
  maxWidth: '1200px',
  padding: spacing.lg,
  paddingMobile: spacing.md,
  borderRadius: '6px',
  border: `1px solid ${colors.borderLight}`
};

// Interactive elements
export const interactive = {
  button: {
    padding: '6px 12px',
    borderRadius: containers.borderRadius,
    border: `1px solid ${colors.border}`,
    background: colors.background,
    cursor: 'pointer',
    fontSize: typography.small.fontSize,
    transition: 'all 0.2s ease',
    ':hover': {
      background: colors.backgroundHover,
      borderColor: colors.border
    }
  },
  link: {
    color: colors.link,
    textDecoration: 'none',
    ':hover': {
      color: colors.linkHover,
      textDecoration: 'underline'
    }
  },
  input: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '13px',
    border: `1px solid ${colors.border}`,
    borderRadius: containers.borderRadius,
    ':focus': {
      outline: 'none',
      borderColor: colors.primary,
      boxShadow: `0 0 0 3px ${colors.primary}20`
    }
  }
};

// Section styling
export const sections = {
  marginBottom: spacing.lg,
  padding: spacing.md,
  borderRadius: containers.borderRadius,
  border: `1px solid ${colors.borderLight}`,
  heading: {
    ...typography.h2,
    display: 'flex',
    alignItems: 'center',
    gap: spacing.sm
  },
  content: {
    marginTop: spacing.sm,
    ...typography.body
  }
};

// Vertical tab navigation (for sidebar)
export const verticalTabs = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.xs,
    marginBottom: spacing.lg,
    paddingBottom: spacing.lg,
    borderBottom: `1px solid ${colors.borderLight}`
  },
  tab: {
    display: 'flex',
    alignItems: 'center',
    padding: `${spacing.sm} ${spacing.md}`,
    fontSize: typography.body.fontSize,
    fontWeight: '500',
    color: colors.textSecondary,
    textDecoration: 'none',
    borderRadius: containers.borderRadius,
    transition: 'all 0.2s ease',
    cursor: 'pointer',
    border: 'none',
    background: 'transparent',
    textAlign: 'left',
    width: '100%',
    position: 'relative'
  },
  tabActive: {
    color: colors.primary,
    fontWeight: '600',
    background: colors.primaryLight,
    borderLeft: `3px solid ${colors.primary}`
  },
  tabHover: {
    background: colors.backgroundHover,
    color: colors.text
  }
};

// Sidebar styling
export const sidebar = {
  position: 'sticky',
  top: '76px',
  height: 'calc(100vh - 96px)',
  maxHeight: 'calc(100vh - 96px)',
  overflowY: 'auto',
  overflowX: 'hidden',
  paddingRight: spacing.sm,
  borderRight: `1px solid ${colors.borderLight}`,
  width: '280px',
  minWidth: '280px',
  maxWidth: '280px',
  scrollBehavior: 'smooth',
  willChange: 'transform',
  transform: 'translateZ(0)' // Create new stacking context for better performance
};

// Main content styling
export const mainContent = {
  paddingBottom: spacing.xxl,
  flex: 1
};

// Layout grid
export const layout = {
  display: 'grid',
  gridTemplateColumns: '280px 1fr',
  gap: spacing.lg,
  alignItems: 'start'
};

// Notice/Alert styling
export const notice = {
  info: {
    backgroundColor: colors.primaryLight,
    border: `1px solid ${colors.primary}`,
    borderRadius: containers.borderRadius,
    padding: spacing.md,
    color: colors.text
  },
  warning: {
    backgroundColor: 'rgba(245, 158, 11, 0.1)', // amber-500 with opacity
    border: '1px solid #f59e0b',
    borderRadius: containers.borderRadius,
    padding: spacing.md,
    color: colors.text
  },
  heading: {
    ...typography.h3,
    fontSize: '16px',
    fontWeight: '600',
    marginBottom: spacing.sm,
    color: colors.text
  },
  text: {
    ...typography.body,
    margin: 0,
    lineHeight: 1.6,
    color: colors.text
  },
  link: {
    color: colors.link,
    textDecoration: 'underline',
    fontWeight: '500',
    ':hover': {
      color: colors.linkHover
    }
  }
};




